// Bespoke voxel prefabs (fiends, covert and zealot units). Split out of creatures_extra.ts, whose
// CREATURE_PREFABS_EXTRA registry still assembles them.

import { box } from './creatures_extra_shared.js';
import type { CharacterPrefab } from './types.js';

// ─── Demon (demon — unit kind 37) ───────────────────────────
// Fiendish humanoid: red skin, horns, glowing eyes, clawed hands, hooves.
// Taller than a stock human (~18 voxels). Menacing posture with clawed
// arms held slightly away from the body.
export const demon: CharacterPrefab = {
  name: 'Demon',
  description: 'Fiendish humanoid with horns, glowing eyes, and hooves',
  maxHp: 45,
  parts: [
    {
      name: 'head',
      voxels: [
        ...box(-1, 12, -1, 3, 4, 3, '#8b1a1a'),
        // Brow ridge
        { x: -1, y: 14, z: 2, color: '#5a0a0a' },
        { x: 1, y: 14, z: 2, color: '#5a0a0a' },
      ],
    },
    {
      name: 'horns',
      voxels: [
        // Left horn curving up and outward
        { x: -2, y: 15, z: 0, color: '#2a1a0a' },
        { x: -3, y: 16, z: 0, color: '#2a1a0a' },
        { x: -3, y: 17, z: 0, color: '#1a0a00' },
        // Right horn curving up and outward
        { x: 2, y: 15, z: 0, color: '#2a1a0a' },
        { x: 3, y: 16, z: 0, color: '#2a1a0a' },
        { x: 3, y: 17, z: 0, color: '#1a0a00' },
      ],
    },
    {
      name: 'eyes',
      voxels: [
        { x: -1, y: 13, z: 2, color: '#ffaa00', emissive: '#ff6600' },
        { x: 1, y: 13, z: 2, color: '#ffaa00', emissive: '#ff6600' },
      ],
    },
    {
      name: 'torso',
      voxels: [
        ...box(-2, 6, -1, 5, 6, 3, '#6a0a0a'),
        // Chest muscle definition
        { x: -1, y: 9, z: 2, color: '#8b1a1a' },
        { x: 1, y: 9, z: 2, color: '#8b1a1a' },
        { x: 0, y: 7, z: 2, color: '#5a0000' },
      ],
    },
    {
      name: 'left_arm_claw',
      voxels: [
        ...box(-3, 6, 0, 1, 6, 1, '#8b1a1a'),
        // Claws spreading wide at the end
        { x: -3, y: 12, z: -1, color: '#1a0a00' },
        { x: -3, y: 12, z: 1, color: '#1a0a00' },
        { x: -4, y: 12, z: 0, color: '#1a0a00' },
      ],
    },
    {
      name: 'right_arm_claw',
      voxels: [
        ...box(3, 6, 0, 1, 6, 1, '#8b1a1a'),
        // Claws spreading wide at the end
        { x: 3, y: 12, z: -1, color: '#1a0a00' },
        { x: 3, y: 12, z: 1, color: '#1a0a00' },
        { x: 4, y: 12, z: 0, color: '#1a0a00' },
      ],
    },
    {
      name: 'legs',
      voxels: [
        // Reverse-jointed demon legs (thigh forward, shin back)
        ...box(-2, 2, 0, 2, 4, 1, '#6a0a0a'),
        ...box(1, 2, 0, 2, 4, 1, '#6a0a0a'),
        ...box(-2, 0, -1, 2, 2, 1, '#5a0000'),
        ...box(1, 0, -1, 2, 2, 1, '#5a0000'),
      ],
    },
    {
      name: 'hooves',
      voxels: [
        { x: -2, y: 0, z: 0, color: '#1a0a00' },
        { x: -1, y: 0, z: 0, color: '#1a0a00' },
        { x: 1, y: 0, z: 0, color: '#1a0a00' },
        { x: 2, y: 0, z: 0, color: '#1a0a00' },
      ],
    },
  ],
};

// ─── Spy (spy — unit kind 38) ───────────────────────────────
// Covert agent: dark hooded cloak, masked face, slim build, dagger at belt.
// Reads as a slinking silhouette — narrow profile, deep hood, no skin showing.
export const spy: CharacterPrefab = {
  name: 'Spy',
  description: 'Covert agent in a dark hooded cloak with a dagger',
  maxHp: 12,
  parts: [
    {
      name: 'hood',
      voxels: [
        ...box(-1, 11, -1, 3, 3, 3, '#1a1a1a'),
        // Hood opening (face shadow)
        { x: 0, y: 12, z: 2, color: '#0a0a0a' },
      ],
    },
    {
      name: 'mask',
      voxels: [
        // Pale mask strip visible inside the hood
        { x: -1, y: 12, z: 2, color: '#6a6a6a' },
        { x: 1, y: 12, z: 2, color: '#6a6a6a' },
        // Eye slits glowing faint red
        { x: -1, y: 13, z: 2, color: '#8b0000', emissive: '#4a0000' },
        { x: 1, y: 13, z: 2, color: '#8b0000', emissive: '#4a0000' },
      ],
    },
    {
      name: 'cloak',
      voxels: [
        // Slim torso wrap
        ...box(-1, 6, -1, 3, 6, 3, '#1a1a1a'),
        // Cloak falls past knees at the back
        ...box(-1, 2, -1, 3, 4, 1, '#0a0a0a'),
      ],
    },
    {
      name: 'left_arm',
      voxels: box(-2, 6, 0, 1, 5, 1, '#1a1a1a'),
    },
    {
      name: 'right_arm',
      voxels: box(2, 6, 0, 1, 5, 1, '#1a1a1a'),
    },
    {
      name: 'legs',
      voxels: [...box(-1, 0, 0, 1, 6, 1, '#0a0a0a'), ...box(1, 0, 0, 1, 6, 1, '#0a0a0a')],
    },
    {
      name: 'dagger_belt',
      voxels: [
        // Belt
        ...box(-1, 5, 1, 3, 1, 1, '#3a2a1a'),
        // Dagger hilt poking out at the left hip
        { x: -2, y: 5, z: 1, color: '#5a3a1a' },
        { x: -2, y: 4, z: 1, color: '#5a3a1a' },
        // Pommel
        { x: -2, y: 3, z: 1, color: '#aaaaaa' },
      ],
    },
  ],
};

// ─── Zealot (zealot) ────────────────────────────────────────
// Fanatical warrior-monk: bald head with ritual tattoos (dark lines on skin),
// torn robes over armor, holy symbol on the chest.
export const zealot: CharacterPrefab = {
  name: 'Zealot',
  description: 'Fanatical warrior-monk with tattoos and torn robes',
  maxHp: 28,
  parts: [
    {
      name: 'head',
      voxels: [
        ...box(-1, 11, -1, 3, 3, 3, '#c9a063'),
        // Ritual tattoo lines crossing the face
        { x: -1, y: 12, z: 2, color: '#3a1a0a' },
        { x: 1, y: 12, z: 2, color: '#3a1a0a' },
        { x: 0, y: 11, z: 2, color: '#3a1a0a' },
        { x: 0, y: 13, z: 2, color: '#3a1a0a' },
      ],
    },
    {
      name: 'tonsure',
      // Shaved bald crown with a darker ring of stubble
      voxels: [
        ...box(-1, 13, -1, 3, 1, 3, '#a08050'),
        { x: -1, y: 14, z: 0, color: '#3a2a1a' },
        { x: 1, y: 14, z: 0, color: '#3a2a1a' },
      ],
    },
    {
      name: 'torso_robed',
      voxels: [
        // Tattered brown robe over rusted chainmail
        ...box(-2, 6, -1, 5, 5, 3, '#5a3a2a'),
        // Chainmail peeking through at the chest
        { x: -1, y: 7, z: 2, color: '#6a6a6a' },
        { x: 0, y: 7, z: 2, color: '#6a6a6a' },
        { x: 1, y: 7, z: 2, color: '#6a6a6a' },
        // Tattered hem below the belt
        ...box(-2, 4, 1, 5, 2, 1, '#3a2a1a'),
      ],
    },
    {
      name: 'holy_symbol',
      // Crimson ankh / sun-symbol on the chest
      voxels: [
        { x: 0, y: 9, z: 2, color: '#8b1a1a', emissive: '#5a0a0a' },
        { x: 0, y: 10, z: 2, color: '#8b1a1a', emissive: '#5a0a0a' },
        { x: -1, y: 10, z: 2, color: '#8b1a1a' },
        { x: 1, y: 10, z: 2, color: '#8b1a1a' },
      ],
    },
    {
      name: 'left_arm',
      voxels: box(-3, 6, 0, 1, 5, 1, '#5a3a2a'),
    },
    {
      name: 'right_arm_blade',
      voxels: [
        ...box(3, 6, 0, 1, 4, 1, '#5a3a2a'),
        // Rusty ritual blade
        ...box(3, 10, 0, 1, 5, 1, '#8a7a5a'),
        { x: 3, y: 9, z: 0, color: '#3a2a1a' },
      ],
    },
    {
      name: 'legs',
      voxels: [...box(-2, 0, 0, 2, 5, 1, '#3a2a1a'), ...box(1, 0, 0, 2, 5, 1, '#3a2a1a')],
    },
  ],
};

// ─── Blood Priest (blood_priest) ────────────────────────────
// Dark priest: deep crimson robes, pallid mask, hood pulled up, holding a
// chalice. Reads as a cult leader rather than a generic spell-caster.
export const bloodPriest: CharacterPrefab = {
  name: 'Blood Priest',
  description: 'Dark priest in crimson robes holding a chalice',
  maxHp: 24,
  parts: [
    {
      name: 'hood',
      voxels: [
        ...box(-1, 11, -1, 3, 3, 3, '#7a1212'),
        // Hood shadow
        { x: 0, y: 12, z: 2, color: '#1a0a0a' },
      ],
    },
    {
      name: 'mask',
      voxels: [
        // Pallid porcelain mask
        { x: -1, y: 12, z: 2, color: '#d8c8b8' },
        { x: 1, y: 12, z: 2, color: '#d8c8b8' },
        // Black eye sockets
        { x: -1, y: 13, z: 2, color: '#1a0a0a' },
        { x: 1, y: 13, z: 2, color: '#1a0a0a' },
      ],
    },
    {
      name: 'robe',
      voxels: [
        // Flowing crimson robe — wide at the hem
        ...box(-2, 0, -2, 5, 8, 5, '#7a1212'),
        // Darker inner lining visible at collar
        ...box(-1, 7, -1, 3, 1, 3, '#4a0808'),
        // Gold trim at the hem
        ...box(-2, 0, -2, 5, 1, 5, '#aa8830'),
      ],
    },
    {
      name: 'stole',
      // Ceremonial stole draped across the chest
      voxels: [
        { x: -1, y: 7, z: 2, color: '#1a1a1a' },
        { x: 0, y: 7, z: 2, color: '#1a1a1a' },
        { x: 1, y: 7, z: 2, color: '#1a1a1a' },
        { x: 0, y: 6, z: 2, color: '#1a1a1a' },
      ],
    },
    {
      name: 'left_arm_chalice',
      voxels: [
        ...box(-3, 6, 0, 1, 4, 1, '#7a1212'),
        // Pale hand
        { x: -3, y: 10, z: 0, color: '#d8c8b8' },
        // Golden chalice
        { x: -3, y: 11, z: 0, color: '#aa8830', metalness: 1.0, roughness: 0.4 },
        // Blood inside the chalice — faint glow
        { x: -3, y: 12, z: 0, color: '#8b0000', emissive: '#4a0000' },
      ],
    },
    {
      name: 'right_arm',
      voxels: [
        ...box(3, 6, 0, 1, 5, 1, '#7a1212'),
        // Pale hand
        { x: 3, y: 11, z: 0, color: '#d8c8b8' },
      ],
    },
  ],
};
